import React, { useState, useEffect } from 'react';
import { useCanvas } from '../../../context/CanvasContext';
import styles from './styles/UploadModal.module.css';
import MediaPlayer from '../../../utils/MediaPlayer';
import { getMediaSrc } from '../../../utils/utils';

/**
 * Uploads one media asset onto a situation, a choice, or a group.
 *
 * The upload and the edit are two steps: the file goes to the media endpoint, and
 * only once it has a URL does an ordinary data op record it. A failed upload
 * therefore leaves the draft exactly as it was, instead of half-applying an edit
 * the way the old combined multipart endpoints could.
 *
 * All three targets take that path now. Groups used to be the exception, with a
 * multipart PATCH straight onto the live document — which meant a group's media
 * reached players the moment it uploaded.
 *
 * @param {Object} props
 * @param {'image'|'document'|'audiovideo'} props.uploadType
 * @param {{kind: 'node'|'edge'|'group', id: string}} props.target
 * @param {Function} props.setIsModalOpen
 */
const UPLOAD_KIND = { image: 'image', document: 'document', audiovideo: 'audio' };

const ACCEPT = {
    image: 'image/*',
    document: 'application/pdf',
    audiovideo: 'audio/*,video/*',
};

const MAX_BYTES = 50 * 1024 * 1024;

const UploadModal = ({ uploadType, target, setIsModalOpen }) => {
    const { uploadMedia, getNode, getEdge, getGroup } = useCanvas();

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [saving, setSaving] = useState(false);

    const field = UPLOAD_KIND[uploadType];

    useEffect(() => {
        let existing = null;
        if (target?.kind === 'group') existing = getGroup(target.id)?.[field] ?? null;
        else if (target?.kind === 'edge') existing = getEdge(target.id)?.data?.[field] ?? null;
        else if (target?.kind === 'node') existing = getNode(target.id)?.data?.[field] ?? null;

        setPreview(existing || null);
        setFile(null);
        setErrorMessage('');
    }, [target?.kind, target?.id, field, getGroup, getEdge, getNode]);

    const handleFileChange = (event) => {
        const selected = event.target.files[0];
        setErrorMessage('');
        setFile(null);
        setPreview(null);

        if (!selected) return;

        if (selected.size > MAX_BYTES) {
            setErrorMessage(`File size exceeds ${MAX_BYTES / 1024 / 1024}MB limit.`);
            return;
        }
        if (uploadType === 'image' && !selected.type.startsWith('image/')) {
            setErrorMessage('Invalid file type. Please upload an image.');
            return;
        }
        if (
            uploadType === 'audiovideo' &&
            !selected.type.startsWith('audio/') &&
            !selected.type.startsWith('video/')
        ) {
            setErrorMessage('Invalid file type. Please upload an audio or video file.');
            return;
        }
        if (uploadType === 'document' && selected.type !== 'application/pdf') {
            setErrorMessage('Invalid file type. Please upload a PDF.');
            return;
        }

        setFile(selected);

        if (uploadType === 'document') {
            setPreview(selected.name);
        } else {
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(selected);
        }
    };

    const handleSave = async () => {
        if (!file) {
            setErrorMessage('No file selected to save.');
            return;
        }
        if (!target?.id) {
            setErrorMessage('Nothing is selected to attach this to.');
            return;
        }

        setSaving(true);
        setErrorMessage('');

        try {
            await uploadMedia({
                kind: field,
                file,
                target: target.kind,
                id: target.id,
            });

            setIsModalOpen(false);
        } catch (error) {
            console.error(`Error updating ${uploadType}: `, error);
            const backendError =
                error.response?.data?.message || 'An error occurred while uploading.';
            setErrorMessage(`${backendError} Please try again.`);
        } finally {
            setSaving(false);
        }
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFile(null);
        setPreview(null);
        setErrorMessage('');
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>
                    Upload{' '}
                    {uploadType === 'image'
                        ? 'Image'
                        : uploadType === 'document'
                        ? 'PDF'
                        : 'Audio/Video'}
                </h2>

                <label
                    htmlFor="file-upload-input"
                    className={`${styles.buttonBase} ${styles.uploadButton} ${
                        saving ? styles.disabled : ''
                    }`}
                >
                    {preview ? 'Change File' : 'Select File'}
                </label>
                <input
                    type="file"
                    id="file-upload-input"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    accept={ACCEPT[uploadType]}
                    disabled={saving}
                />

                {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}

                {preview && (
                    <div className={styles.previewContainer}>
                        {uploadType === 'image' ? (
                            <img src={preview} alt="Preview" className={styles.previewImage} />
                        ) : uploadType === 'document' ? (
                            preview.startsWith('http') ? (
                                <a href={preview} target="_blank" rel="noopener noreferrer">
                                    View current PDF
                                </a>
                            ) : (
                                <p className={styles.fileName}>{preview}</p>
                            )
                        ) : (
                            <MediaPlayer
                                src={getMediaSrc(preview)}
                                autoplay={false}
                                key={getMediaSrc(preview)}
                            />
                        )}
                        {file && uploadType !== 'document' && (
                            <p className={styles.fileName}>Selected: {file.name}</p>
                        )}
                    </div>
                )}

                <div className={styles.buttonGroup}>
                    <button
                        onClick={handleSave}
                        className={`${styles.buttonBase} ${styles.saveButton}`}
                        disabled={!file || saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        onClick={closeModal}
                        className={`${styles.buttonBase} ${styles.cancelButton}`}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UploadModal;
